import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Users, Laptop2, Award, Instagram, Sparkles } from "lucide-react";
import { BRAND, MEDIA } from "@/lib/data";

const cardMotion = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

function ChoiceCard({ to, icon: Icon, kicker, title, subtitle, image, testid, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className="group relative"
    >
      <Link
        to={to}
        data-testid={testid}
        className="relative block overflow-hidden rounded-3xl border border-white/10 bg-neutral-950 hover:border-yellow-400/40 transition-all duration-500 h-full"
      >
        <div className="relative aspect-[4/5] sm:aspect-[4/3] lg:aspect-[5/6] overflow-hidden">
          <img
            src={image}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/10" />
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-orange-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

          <div className="absolute top-6 left-6 flex items-center gap-2 rounded-full border border-white/15 bg-black/60 backdrop-blur px-3 py-1.5 text-[10px] sm:text-xs font-bold tracking-[0.18em] uppercase text-yellow-400">
            <Sparkles className="w-3 h-3" /> {kicker}
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 lg:p-10">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 text-black mb-5 shadow-[0_10px_30px_rgba(250,204,21,0.35)]">
              <Icon className="w-6 h-6" strokeWidth={2.4} />
            </div>
            <h3 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.02]">
              {title}
            </h3>
            <p className="mt-3 text-sm sm:text-base text-neutral-300 max-w-md leading-relaxed">
              {subtitle}
            </p>
            <div className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-yellow-400 group-hover:gap-4 transition-all">
              Saiba mais <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default function Home() {
  return (
    <div className="relative">
      {/* Hero / Choice */}
      <section className="relative min-h-screen flex flex-col justify-start overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <img
            src={MEDIA.gymEnv}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/80 to-black" />
          <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-yellow-500/10 blur-[140px]" />
          <div className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-orange-500/10 blur-[140px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8 pt-28 sm:pt-36 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-yellow-400/30 bg-yellow-400/5 px-3 py-1 text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase text-yellow-400">
              <Award className="w-3 h-3" /> {BRAND.professional} · {BRAND.cref}
            </span>
            <h1 className="font-heading font-black tracking-[-0.03em] text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
              <span className="text-white">Escolha o seu </span>
              <span className="text-gradient-yo">caminho</span>
              <br />
              <span className="text-white">para o próximo nível.</span>
            </h1>
            <p className="text-neutral-400 text-base sm:text-lg max-w-xl leading-relaxed">
              Dois métodos, um só objetivo: te levar à sua melhor versão com ciência, estratégia e acompanhamento real.
            </p>
            <a
              href={BRAND.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-instagram-cta"
              className="inline-flex items-center gap-2 text-xs font-medium text-neutral-400 hover:text-yellow-400 transition-colors"
            >
              <Instagram className="w-4 h-4" /> @{BRAND.instagram}
            </a>
          </motion.div>

          {/* Two choice cards */}
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            <ChoiceCard
              to="/personal"
              icon={Users}
              kicker="Presencial · Foz do Iguaçu"
              title={<>Personal<br/>Trainer</>}
              subtitle="Treino presencial com foco máximo nos seus resultados. Correção em tempo real, motivação lado a lado."
              image={MEDIA.trainerPortrait}
              testid="home-card-personal"
              delay={0.1}
            />
            <ChoiceCard
              to="/online"
              icon={Laptop2}
              kicker="Online · De onde você estiver"
              title={<>Consultoria<br/>Online</>}
              subtitle="Transformação com método científico, app MFIT no celular e suporte direto comigo pelo WhatsApp."
              image={MEDIA.gymEnv}
              testid="home-card-online"
              delay={0.25}
            />
          </div>

          {/* Trust strip */}
          <motion.div
            {...cardMotion}
            className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 text-center"
          >
            {[
              { n: "+10", l: "Avaliações 5⭐ no Google" },
              { n: "7+", l: "Anos no movimento" },
              { n: "100%", l: "Método baseado em ciência" },
              { n: "24h", l: "Anamnese pós-pagamento" },
            ].map((s) => (
              <div
                key={s.l}
                className="glass-card rounded-2xl px-3 py-5 sm:py-7"
              >
                <div className="font-heading text-2xl sm:text-3xl font-black text-gradient-yo">
                  {s.n}
                </div>
                <div className="mt-1 text-[11px] sm:text-xs text-neutral-400 uppercase tracking-wider">
                  {s.l}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
